import { lazy, Suspense, useEffect, useRef, useState } from 'react'
import QRCode from 'qrcode'
import { api, ApiError } from './api'
import { AuthProvider, useAuth } from './auth'
import './App.css'

const ProtectedApp = lazy(() => import('./protected/ProtectedApp'))

type Invite = {
  email: string
  display_name: string
  role: string
  totp_uri: string
  totp_secret_token: string
}

function BlogFrame({ children }: { children: React.ReactNode }) {
  return (
    <main className="blog-page">
      <div className="blog-wrap">
        <div className="blog-name">Notes by Luiz</div>
        {children}
        <p className="blog-foot">A private collection of essays and working notes.</p>
      </div>
    </main>
  )
}

function SignIn({ onContinue }: { onContinue: () => void }) {
  const { login } = useAuth()
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  return (
    <BlogFrame>
      <form
        className="blog-card"
        onSubmit={async (event) => {
          event.preventDefault()
          setBusy(true)
          setError('')
          const form = new FormData(event.currentTarget)
          try {
            await login(String(form.get('email')), String(form.get('password')))
            onContinue()
          } catch (reason) {
            setError(reason instanceof ApiError ? reason.message : 'Unable to sign in.')
          } finally {
            setBusy(false)
          }
        }}
      >
        <h1>Welcome back</h1>
        <p className="muted">Sign in to continue reading.</p>
        {error && <div className="auth-error" role="alert">{error}</div>}
        <label>
          <span>Email</span>
          <input name="email" type="email" autoComplete="username" required />
        </label>
        <label>
          <span>Password</span>
          <input name="password" type="password" autoComplete="current-password" minLength={8} required />
        </label>
        <button className="blog-button" disabled={busy}>{busy ? 'Checking…' : 'Continue'}</button>
      </form>
    </BlogFrame>
  )
}

function Verify({ onBack }: { onBack: () => void }) {
  const { verify, verifyRecovery } = useAuth()
  const [digits, setDigits] = useState(['', '', '', '', '', ''])
  const [recovery, setRecovery] = useState(false)
  const [recoveryCode, setRecoveryCode] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const refs = useRef<Array<HTMLInputElement | null>>([])
  const code = digits.join('')

  return (
    <BlogFrame>
      <form
        className="blog-card"
        onSubmit={async (event) => {
          event.preventDefault()
          if (!recovery && code.length !== 6) return setError('Enter the six-digit code.')
          if (recovery && recoveryCode.length < 8) return setError('Enter a recovery code.')
          setBusy(true)
          setError('')
          try {
            if (recovery) await verifyRecovery(recoveryCode)
            else await verify(code)
          } catch (reason) {
            setError(reason instanceof ApiError ? reason.message : 'Verification failed.')
            setDigits(['', '', '', '', '', ''])
            refs.current[0]?.focus()
          } finally {
            setBusy(false)
          }
        }}
      >
        <h1>Verify access</h1>
        <p className="muted">{recovery ? 'Enter one of your saved recovery codes.' : 'Enter the current code from your authenticator.'}</p>
        {error && <div className="auth-error" role="alert">{error}</div>}
        {recovery ? <label><span>Recovery code</span><input value={recoveryCode} onChange={(event) => setRecoveryCode(event.target.value)} autoComplete="one-time-code" /></label> : <div className="code-boxes" onPaste={(event) => {
          const value = event.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6)
          if (value) {
            event.preventDefault()
            setDigits(Array.from({ length: 6 }, (_, index) => value[index] || ''))
            refs.current[Math.min(value.length, 5)]?.focus()
          }
        }}>
          {digits.map((digit, index) => (
            <input
              key={index}
              ref={(element) => { refs.current[index] = element }}
              value={digit}
              aria-label={`Digit ${index + 1}`}
              inputMode="numeric"
              maxLength={1}
              onChange={(event) => {
                const value = event.target.value.replace(/\D/g, '').slice(-1)
                setDigits((current) => current.map((item, at) => at === index ? value : item))
                if (value) refs.current[index + 1]?.focus()
              }}
              onKeyDown={(event) => {
                if (event.key === 'Backspace' && !digit) refs.current[index - 1]?.focus()
              }}
            />
          ))}
        </div>}
        <button className="blog-button" disabled={busy || (recovery ? recoveryCode.length < 8 : code.length !== 6)}>
          {busy ? 'Verifying…' : 'Verify'}
        </button>
        <button type="button" className="text-button" onClick={() => { setRecovery(!recovery); setError('') }}>{recovery ? 'Use authenticator instead' : 'Use a recovery code'}</button>
        <button type="button" className="text-button" onClick={onBack}>Back to sign in</button>
      </form>
    </BlogFrame>
  )
}

function Setup({ token }: { token: string }) {
  const [invite, setInvite] = useState<Invite | null>(null)
  const [qr, setQr] = useState('')
  const [error, setError] = useState('')
  const [recovery, setRecovery] = useState<string[] | null>(null)

  useEffect(() => {
    api<Invite>(`/invitations/${encodeURIComponent(token)}`)
      .then(async (value) => {
        setInvite(value)
        setQr(await QRCode.toDataURL(value.totp_uri, { width: 220, margin: 1 }))
      })
      .catch((reason) => setError(reason instanceof ApiError ? reason.message : 'This invitation is unavailable.'))
  }, [token])

  if (recovery) return (
    <BlogFrame>
      <section className="blog-card">
        <h1>Save recovery codes</h1>
        <p className="muted">Store these somewhere private. Each code works once.</p>
        <pre className="recovery-codes">{recovery.join('\n')}</pre>
        <a className="blog-button link-button" href="/">I saved them</a>
      </section>
    </BlogFrame>
  )

  return (
    <BlogFrame>
      <form className="blog-card" onSubmit={async (event) => {
        event.preventDefault()
        if (!invite) return
        setError('')
        const form = new FormData(event.currentTarget)
        try {
          const result = await api<{ recovery_codes: string[] }>(
            `/invitations/${encodeURIComponent(token)}/enroll`,
            {
              method: 'POST',
              json: {
                password: form.get('password'),
                totp_code: form.get('code'),
                totp_secret_token: invite.totp_secret_token,
              },
            },
          )
          setRecovery(result.recovery_codes)
        } catch (reason) {
          setError(reason instanceof ApiError ? reason.message : 'Setup could not be completed.')
        }
      }}>
        <h1>Set up access</h1>
        <p className="muted">{invite ? `Invitation for ${invite.email}` : 'Checking your invitation…'}</p>
        {error && <div className="auth-error" role="alert">{error}</div>}
        {invite && <>
          <label>
            <span>Create password</span>
            <input name="password" type="password" minLength={12} autoComplete="new-password" required />
          </label>
          <div className="qr-wrap">
            {qr && <img src={qr} width="220" height="220" alt="Authenticator setup QR code" />}
            <p>Scan this code with your authenticator app.</p>
          </div>
          <label>
            <span>Six-digit authenticator code</span>
            <input name="code" inputMode="numeric" pattern="\d{6}" maxLength={6} required />
          </label>
          <button className="blog-button">Finish setup</button>
        </>}
      </form>
    </BlogFrame>
  )
}

function AccessRouter() {
  const { user, loading } = useAuth()
  const [step, setStep] = useState<'signin' | 'verify'>('signin')
  const params = new URLSearchParams(location.search)
  const token = params.get('token')

  useEffect(() => {
    if (!loading && !user) setStep('signin')
  }, [loading, user])

  if (token && location.pathname === '/setup') return <Setup token={token} />
  if (loading) return <BlogFrame><div className="blog-card"><p>Opening notes…</p></div></BlogFrame>
  if (user) return <Suspense fallback={<div className="app-loading">Loading…</div>}><ProtectedApp /></Suspense>
  return step === 'verify'
    ? <Verify onBack={() => setStep('signin')} />
    : <SignIn onContinue={() => setStep('verify')} />
}

export default function App() {
  return <AuthProvider><AccessRouter /></AuthProvider>
}
